// useHospitalEdition — the "AHP edition" of a hospital's price file, written
// by scripts/generate_hospital_edition.py next to the original in the R2
// archive (chargemaster-archive/<uuid>/<year>/). The archive tab reads the
// manifest first (one small JSON); the deduplicated lines and the original's
// first lines load on demand. A 404 means no edition has been generated for
// that hospital/year yet — the tab then falls back to the imported preview.
import { useQuery } from "@tanstack/react-query";

export const ARCHIVE_BASE = "https://static.americanhealthprices.com/chargemaster-archive";

export interface EditionManifest {
  v: number;
  generated_at: string;
  hospital_id: string;
  hospital_name: string;
  year: string;
  file: { name: string | null; type: string | null; size_bytes: number | null; rows_in_original: number | null; url: string | null; published_at: string | null };
  counts: { lines: number; raw_rows: number; flagged: number; with_us_median: number; families: Record<string, number>; findings: number; findings_by_severity: Record<string, number>; nearby?: Record<string, number>; nearby_hospitals?: number };
  nearby_rule?: string;
  columns: string[];
  column_labels: Record<string, string>;
  files: { xlsx: string; csv: string; rows: string; head: string; report_html: string; report_pdf: string | null };
  head: { available: boolean; note: string; lines: number };
  position: string | null;
  dedup_rule: string;
}

/** nb30/nb60/nb120 cells are [index into `hospitals`, price, straight-line miles] or null. */
export type NearbyPick = [number, number, number];
export interface EditionRows {
  v?: number;
  generated_at: string;
  cols: string[];
  rows: (string | number | null | NearbyPick)[][];
  /** [id, name, city, state] for every hospital referenced by a nearby pick. */
  hospitals?: [string, string, string, string][];
  nearby_rule?: string;
}

export function editionUrl(hospitalId: string, year: string | number, file: string) {
  return `${ARCHIVE_BASE}/${hospitalId}/${year}/${file}`;
}

async function fetchJson<T>(url: string, revalidate = false): Promise<T | null> {
  // The manifest is tiny and is what changes on re-ingest, so always
  // revalidate it at the edge; the row file is keyed by generated_at.
  const r = await fetch(url, { headers: { Accept: "application/json" }, cache: revalidate ? "no-cache" : "default" });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${url}`);
  return (await r.json()) as T;
}

export function useHospitalEdition(hospitalId: string | undefined, year: string | number | null | undefined) {
  const enabled = !!hospitalId && year != null && year !== "";
  const manifestQ = useQuery({
    queryKey: ["hospital-edition", hospitalId, String(year)],
    enabled,
    staleTime: 1000 * 60 * 60,
    retry: 1,
    queryFn: () => fetchJson<EditionManifest>(editionUrl(hospitalId!, year!, "edition.json"), true),
  });
  const manifest = manifestQ.data ?? null;
  const rowsQ = useQuery({
    queryKey: ["hospital-edition-rows", hospitalId, String(year), manifest?.generated_at],
    enabled: !!manifest,
    staleTime: 1000 * 60 * 60,
    retry: 1,
    queryFn: () => fetchJson<EditionRows>(`${manifest!.files.rows}?g=${encodeURIComponent(manifest!.generated_at)}`),
  });
  const headQ = useQuery({
    queryKey: ["hospital-edition-head", hospitalId, String(year), manifest?.generated_at],
    enabled: !!manifest && manifest.head.available,
    staleTime: 1000 * 60 * 60,
    retry: 1,
    queryFn: async () => {
      const r = await fetch(manifest!.files.head);
      if (!r.ok) return null;
      const text = await r.text();
      // first line is the generator's "# note"
      return text.replace(/^#[^\n]*\n?/, "");
    },
  });
  return {
    manifest,
    rows: rowsQ.data ?? null,
    head: headQ.data ?? null,
    isLoading: enabled && (manifestQ.isLoading || (!!manifest && rowsQ.isLoading)),
    notFound: enabled && !manifestQ.isLoading && manifest == null,
    error: manifestQ.error || rowsQ.error || null,
  };
}
